
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getTranslationResources, getTranslationKeys } from './translationUtils';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/lib/i18n';

interface LanguageSettingsProps {
  selectedLocale: string;
  availableLocales: string[];
  onLocaleChange: (locale: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  translationKeys: string[];
  reloadTranslations: () => Promise<void>;
}

export function LanguageSettings({
  selectedLocale,
  availableLocales,
  onLocaleChange,
  isLoading,
  setIsLoading,
  translationKeys,
  reloadTranslations
}: LanguageSettingsProps) {
  const { t, i18n: i18nInstance } = useTranslation();
  const [newLocale, setNewLocale] = useState('');
  const [newTranslation, setNewTranslation] = useState({ key: '', value: '' });
  const { toast } = useToast();
  
  const handleAddNewLocale = async () => {
    if (!newLocale || newLocale.length !== 2) {
      toast({
        title: 'Invalid locale code',
        description: t('invalidLocaleCode'),
        variant: 'destructive',
      });
      return;
    }
    
    if (availableLocales.includes(newLocale)) {
      toast({
        title: 'Locale already exists',
        description: t('localeExists', { locale: newLocale }),
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create empty translations by copying from English
      const resources = getTranslationResources();
      const enCommon = resources.en?.common;
      
      if (enCommon && typeof enCommon === 'object') {
        // Add translations for all existing keys with empty values
        const promises = [];
        translationKeys.forEach(key => {
          promises.push(
            supabase.rpc('upsert_translation', {
              p_locale: newLocale,
              p_key: key,
              p_value: ''
            })
          );
        });
        
        // Wait for all database operations to complete
        await Promise.all(promises);
        
        // Reload translations
        if (i18n.reloadResourcesFromDb) {
          await i18n.reloadResourcesFromDb();
        } else {
          await i18n.reloadResources();
        }
        
        setNewLocale('');
        
        toast({
          title: 'Locale added',
          description: t('localeAdded', { locale: newLocale }),
        });
        
        // Reload translations list to show new locale
        await reloadTranslations();
      } else {
        toast({
          title: 'Error',
          description: 'Could not add locale: English translations not found or invalid',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error("Error adding locale:", error);
      toast({
        title: 'Error adding locale',
        description: 'An error occurred while adding the new locale',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddNewTranslationKey = async () => {
    if (!newTranslation.key) {
      toast({
        title: 'Invalid key',
        description: t('invalidKey'),
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Add new key to all locales
      const resources = getTranslationResources();
      const promises = [];
      
      Object.keys(resources).forEach(locale => {
        // Add the new key with the value (for the current locale) or empty string
        const value = locale === selectedLocale ? newTranslation.value : '';
        
        promises.push(
          supabase.rpc('upsert_translation', {
            p_locale: locale,
            p_key: newTranslation.key,
            p_value: value
          })
        );
      });
      
      // Wait for all database operations to complete
      await Promise.all(promises);
      
      // Reload translations
      if (i18n.reloadResourcesFromDb) {
        await i18n.reloadResourcesFromDb();
      } else {
        await i18n.reloadResources();
      }
      
      setNewTranslation({ key: '', value: '' });
      
      // Force UI update
      if (i18nInstance.changeLanguage) {
        const currentLang = i18nInstance.language;
        await i18nInstance.changeLanguage('temp-lang-trigger-reload');
        await i18nInstance.changeLanguage(currentLang);
      }
      
      toast({
        title: 'Translation key added',
        description: t('translationKeyAdded', { key: newTranslation.key }),
      });
      
      // Reload translations list to include new key
      await reloadTranslations();
    } catch (error) {
      console.error("Error adding translation key:", error);
      toast({
        title: 'Error adding translation key',
        description: 'An error occurred while adding the new translation key',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const syncTranslationsToDb = async () => {
    setIsLoading(true);
    toast({
      title: t('syncingTranslations'),
      description: 'Syncing translations from localStorage to database...',
    });
    
    try {
      const resources = getTranslationResources();
      const promises = [];
      
      // Process each locale
      Object.keys(resources).forEach(locale => {
        if (resources[locale]?.common) {
          // Get all keys recursively
          const flattenObject = (obj: any, prefix = '') => {
            return Object.keys(obj).reduce((acc: Record<string, string>, key) => {
              const prefixedKey = prefix ? `${prefix}.${key}` : key;
              
              if (typeof obj[key] === 'object' && obj[key] !== null) {
                Object.assign(acc, flattenObject(obj[key], prefixedKey));
              } else {
                acc[prefixedKey] = obj[key];
              }
              
              return acc;
            }, {});
          };
          
          const flatTranslations = flattenObject(resources[locale].common);
          
          // Add each key-value pair to the database
          Object.entries(flatTranslations).forEach(([key, value]) => {
            promises.push(
              supabase.rpc('upsert_translation', {
                p_locale: locale,
                p_key: key,
                p_value: String(value)
              })
            );
          });
        }
      });
      
      await Promise.all(promises);
      
      toast({
        title: t('success'),
        description: t('translationsSynced'),
      });
    } catch (error) {
      console.error("Error syncing translations to database:", error);
      toast({
        title: 'Error',
        description: t('errorSyncingTranslations'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-sm font-medium">{t('addNewLanguage')}</h3>
        <div className="flex items-center space-x-2">
          <Input
            placeholder={t('languageCode')}
            className="w-36"
            value={newLocale}
            onChange={(e) => setNewLocale(e.target.value.toLowerCase())}
            maxLength={2}
            disabled={isLoading}
          />
          <Button 
            onClick={handleAddNewLocale}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('addLanguage')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('languageCodeHelp')}
        </p>
      </div>
      
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium">{t('addNewTranslationKey')}</h3>
        <div className="grid gap-4">
          <Input
            placeholder={t('translationKeyPlaceholder')}
            value={newTranslation.key}
            onChange={(e) => setNewTranslation(prev => ({ ...prev, key: e.target.value }))}
            disabled={isLoading}
          />
          <Input
            placeholder={t('valueForLocale', { locale: selectedLocale })}
            value={newTranslation.value}
            onChange={(e) => setNewTranslation(prev => ({ ...prev, value: e.target.value }))}
            disabled={isLoading}
          />
          <Button 
            onClick={handleAddNewTranslationKey}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('addTranslationKey')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('translationKeyHelp')}
        </p>
      </div>
      
      <div className="pt-4 border-t">
        <h3 className="text-sm font-medium mb-2">{t('defaultLanguage')}</h3>
        <Select
          value={selectedLocale}
          onValueChange={onLocaleChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('selectLanguage')} />
          </SelectTrigger>
          <SelectContent>
            {availableLocales.map(locale => (
              <SelectItem key={locale} value={locale}>
                {locale === 'en' ? 'English' : 
                 locale === 'es' ? 'Español' : 
                 locale === 'fr' ? 'Français' : 
                 locale}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="pt-4 border-t">
        <h3 className="text-sm font-medium mb-2">Database Sync</h3>
        <div className="flex flex-col space-y-4">
          <Button 
            onClick={syncTranslationsToDb}
            disabled={isLoading}
            variant="secondary"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Sync Current Translations to Database
          </Button>
          <Button 
            onClick={reloadTranslations}
            disabled={isLoading}
            variant="outline"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Reload Translations from Database
          </Button>
          <p className="text-xs text-muted-foreground">
            Use these buttons to sync translations between the database and the application.
          </p>
        </div>
      </div>
    </div>
  );
}
